// src/components/forms/ApplicationForm.tsx — take an application, then decide it.
import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ArrowRight, ShieldCheck } from 'lucide-react';
import { api } from '../../lib/api';
import { useCan } from '../../hooks/usePermission';
import { useLeadOptions, useListingOptions, useUserOptions } from '../../lib/options';
import { Badge, Button, Field, Input, Modal, Select, Textarea } from '../ui';
import { FormError, FormGrid, Wide, nulled, numberOr, toMajor, toMinor } from './common';

type A = Record<string, any>;
const RTR = ['PENDING', 'PASSED', 'FAILED', 'NA'];
const EDITABLE_STATUSES = ['DRAFT', 'SUBMITTED', 'REFERENCING', 'WITHDRAWN'];

/**
 * One panel for the whole application: the details while it is open, the Right to
 * Rent check that gates approval, and the decision itself. Approve is refused by the
 * API until the check has passed — that rule lives in the jurisdiction config, so the
 * button simply explains why rather than re-implementing it.
 */
export function ApplicationPanel({ open, onClose, application, onConverted }: { open: boolean; onClose: () => void; application?: A; onConverted?: (tenancyId: string) => void }) {
  const isNew = !application;
  const qc = useQueryClient();
  const listings = useListingOptions(isNew);
  const leads = useLeadOptions(isNew);
  const users = useUserOptions();
  const canApprove = useCan('applications.approve');
  const canReject = useCan('applications.reject');
  const canTenancy = useCan('tenancies.create');

  const [form, setForm] = useState(() => ({
    listingId: application?.listingId ?? '', leadId: application?.leadId ?? '',
    applicantUserId: application?.applicantUserId ?? '',
    name: application?.applicant?.name ?? '', employment: application?.applicant?.employment ?? '',
    income: toMajor(application?.declaredIncomeMinor), rent: toMajor(application?.offeredRentMinor),
    requestedMoveIn: application?.requestedMoveIn ?? '', termMonths: String(application?.termMonths ?? 12),
    status: application?.status ?? 'SUBMITTED',
    rightToRentStatus: application?.rightToRentStatus ?? 'PENDING',
    rightToRentCheckedOn: application?.rightToRentCheckedOn ?? '',
    rightToRentExpiresOn: application?.rightToRentExpiresOn ?? '',
  }));
  const [notes, setNotes] = useState('');
  const set = (patch: Partial<typeof form>) => setForm((f) => ({ ...f, ...patch }));

  const chooseListing = (id: string) => {
    const l = (listings.data ?? []).find((x) => x.id === id);
    set({ listingId: id, ...(l ? { rent: toMajor(l.advertisedRentMinor), termMonths: String(l.minTermMonths ?? 12) } : {}) });
  };

  const done = () => { void qc.invalidateQueries({ queryKey: ['/applications'] }); void qc.invalidateQueries({ queryKey: ['/leads'] }); onClose(); };

  // On an approved application the only thing still editable is the resident account,
  // because converting cannot happen without one.
  const linkOnly = !isNew && (application!.status === 'APPROVED');

  const save = useMutation({
    mutationFn: async () => (linkOnly
      ? (await api.patch<A>(`/applications/${application!.id}`, { applicantUserId: nulled(form.applicantUserId) })).data
      : isNew
      ? (await api.post<A>('/applications', {
          listingId: form.listingId, leadId: nulled(form.leadId), applicantUserId: nulled(form.applicantUserId),
          applicant: { name: form.name, employment: form.employment },
          declaredIncomeMinor: toMinor(form.income) ?? null, offeredRentMinor: toMinor(form.rent) ?? 0,
          requestedMoveIn: nulled(form.requestedMoveIn), termMonths: numberOr(form.termMonths, 12),
        })).data
      : (await api.patch<A>(`/applications/${application!.id}`, {
          applicantUserId: nulled(form.applicantUserId),
          applicant: { name: form.name, employment: form.employment },
          declaredIncomeMinor: toMinor(form.income) ?? null, offeredRentMinor: toMinor(form.rent) ?? 0,
          requestedMoveIn: nulled(form.requestedMoveIn), termMonths: numberOr(form.termMonths, 12),
          status: form.status,
          rightToRentStatus: form.rightToRentStatus,
          rightToRentCheckedOn: nulled(form.rightToRentCheckedOn),
          rightToRentExpiresOn: nulled(form.rightToRentExpiresOn),
        })).data),
    onSuccess: done,
  });

  const decide = useMutation({
    mutationFn: async (action: 'approve' | 'reject') => api.post(`/applications/${application!.id}/${action}`, { notes: notes.trim() || undefined }),
    onSuccess: done,
  });

  const convert = useMutation({
    mutationFn: async () => (await api.post<{ tenancy: A }>(`/applications/${application!.id}/convert`, {})).data,
    onSuccess: (r) => { done(); onConverted?.(r.tenancy.id); },
  });

  const status = application?.status ?? 'DRAFT';
  const editable = isNew || EDITABLE_STATUSES.includes(status);
  const rtrPassed = form.rightToRentStatus === 'PASSED';
  const error = save.error ?? decide.error ?? convert.error;
  const busy = save.isPending || decide.isPending || convert.isPending;

  return (
    <Modal open={open} onClose={onClose} size="lg"
      title={isNew ? 'New application' : application!.reference}
      footer={<>
        <Button variant="secondary" onClick={onClose}>Close</Button>
        {editable && <Button onClick={() => save.mutate()} disabled={!form.listingId || busy}>{save.isPending ? 'Saving…' : isNew ? 'Create application' : 'Save changes'}</Button>}
        {linkOnly && form.applicantUserId !== (application!.applicantUserId ?? '') && (
          <Button variant="secondary" onClick={() => save.mutate()} disabled={busy}>{save.isPending ? 'Linking…' : 'Link account'}</Button>
        )}
        {!isNew && ['SUBMITTED', 'REFERENCING'].includes(status) && <>
          {canReject && <Button variant="secondary" onClick={() => decide.mutate('reject')} disabled={busy}>Reject</Button>}
          {canApprove && <Button onClick={() => decide.mutate('approve')} disabled={busy || !rtrPassed} title={rtrPassed ? '' : 'The Right to Rent check must pass first'}>Approve</Button>}
        </>}
        {!isNew && status === 'APPROVED' && canApprove && canTenancy && (
          <Button onClick={() => convert.mutate()} disabled={busy || !form.applicantUserId}>{convert.isPending ? 'Creating tenancy…' : 'Convert to tenancy'} <ArrowRight className="h-4 w-4" /></Button>
        )}
      </>}>
      <div className="space-y-5">
        {!isNew && (
          <div className="flex flex-wrap items-center gap-2 text-[12.5px] text-ink-500">
            <Badge value={status} />
            <span>{application!.propertyName} · {application!.unitReference}</span>
            {application!.tenancyId && <span className="text-ink-400">· tenancy created</span>}
          </div>
        )}

        <FormGrid>
          {isNew && <>
            <Wide><Field label="Home"><Select value={form.listingId} onChange={(e) => chooseListing(e.target.value)} className="w-full"><option value="">Choose a listing…</option>{(listings.data ?? []).map((l) => <option key={l.id} value={l.id}>{l.headline} · {l.property?.name}</option>)}</Select></Field></Wide>
            <Wide><Field label="From which lead"><Select value={form.leadId} onChange={(e) => set({ leadId: e.target.value })} className="w-full"><option value="">Not linked</option>{(leads.data ?? []).map((l) => <option key={l.id} value={l.id}>{l.name ?? l.email ?? l.phone}</option>)}</Select></Field></Wide>
          </>}

          <Field label="Applicant name"><Input value={form.name} onChange={(e) => set({ name: e.target.value })} disabled={!editable} className="w-full" /></Field>
          <Field label="Employment"><Input value={form.employment} onChange={(e) => set({ employment: e.target.value })} disabled={!editable} placeholder="Employed · NHS" className="w-full" /></Field>

          <Wide>
            <Field label="Resident account" hint="A tenancy needs a user account for the applicant. Create one under Settings → Users if it does not exist yet.">
              <Select value={form.applicantUserId} onChange={(e) => set({ applicantUserId: e.target.value })} disabled={!editable && status !== 'APPROVED'} className="w-full">
                <option value="">Not linked</option>
                {(users.data ?? []).map((u) => <option key={u.id} value={u.id}>{u.firstName} {u.lastName} — {u.email}</option>)}
              </Select>
            </Field>
          </Wide>

          <Field label="Declared income (per year)"><Input value={form.income} onChange={(e) => set({ income: e.target.value })} disabled={!editable} inputMode="decimal" className="w-full" /></Field>
          <Field label="Offered rent (per month)"><Input value={form.rent} onChange={(e) => set({ rent: e.target.value })} disabled={!editable} inputMode="decimal" className="w-full" /></Field>
          <Field label="Requested move in"><Input type="date" value={form.requestedMoveIn} onChange={(e) => set({ requestedMoveIn: e.target.value })} disabled={!editable} className="w-full" /></Field>
          <Field label="Term (months)"><Input value={form.termMonths} onChange={(e) => set({ termMonths: e.target.value })} disabled={!editable} inputMode="numeric" className="w-full" /></Field>
        </FormGrid>

        {!isNew && (
          <div className="rounded-xl border border-ink-200 bg-ink-50/60 p-4">
            <div className="mb-3 flex items-center gap-1.5 text-[12px] font-semibold uppercase tracking-[0.14em] text-ink-500"><ShieldCheck className="h-3.5 w-3.5" /> Right to Rent</div>
            <FormGrid>
              <Field label="Check result"><Select value={form.rightToRentStatus} onChange={(e) => set({ rightToRentStatus: e.target.value })} disabled={!editable} className="w-full">{RTR.map((r) => <option key={r} value={r}>{r === 'NA' ? 'Not applicable' : r[0] + r.slice(1).toLowerCase()}</option>)}</Select></Field>
              <Field label="Checked on"><Input type="date" value={form.rightToRentCheckedOn} onChange={(e) => set({ rightToRentCheckedOn: e.target.value })} disabled={!editable} className="w-full" /></Field>
              <Field label="Expires on" hint="Time-limited leave to remain needs a follow-up check"><Input type="date" value={form.rightToRentExpiresOn} onChange={(e) => set({ rightToRentExpiresOn: e.target.value })} disabled={!editable} className="w-full" /></Field>
              {editable && <Field label="Application stage"><Select value={form.status} onChange={(e) => set({ status: e.target.value })} className="w-full">{EDITABLE_STATUSES.map((s) => <option key={s} value={s}>{s[0] + s.slice(1).toLowerCase()}</option>)}</Select></Field>}
            </FormGrid>
            {!rtrPassed && ['SUBMITTED', 'REFERENCING'].includes(status) && (
              <p className="mt-3 text-[12px] text-amber-700">England requires this check before a tenancy may start, so approval stays locked until it passes.</p>
            )}
          </div>
        )}

        {!isNew && ['SUBMITTED', 'REFERENCING'].includes(status) && (canApprove || canReject) && (
          <Field label="Decision notes" hint="Kept on the application and the audit trail"><Textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} className="w-full" /></Field>
        )}

        {!isNew && status === 'APPROVED' && !form.applicantUserId && (
          <p className="rounded-xl border border-amber-200 bg-amber-50 px-3.5 py-2.5 text-[12.5px] text-amber-800">
            Link a resident account above before converting — the tenancy needs someone to belong to.
          </p>
        )}
        {!isNew && status === 'APPROVED' && form.applicantUserId && (
          <p className="rounded-xl border border-ink-200 bg-ink-50/60 px-3.5 py-2.5 text-[12.5px] text-ink-600">
            Converting creates the tenancy on the jurisdiction's terms — fixed term in the UAE, open periodic in England — and marks the lead converted. Rent and the deposit are generated when you activate it.
          </p>
        )}

        {application?.decisionNotes && <p className="text-[12.5px] text-ink-500">Decision: {application.decisionNotes}</p>}
        <FormError error={error} />
      </div>
    </Modal>
  );
}
